import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, DollarSign, Home, CheckCircle, XCircle, AlertCircle, Eye, EyeOff, ChevronLeft, X } from 'lucide-react';
import { fetchUnits, createUnit, updateUnit, deleteUnit } from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

const UnitManagement = ({ currentApartment, selectedCategory, handleBackToApartments, handleBackToCategories, onCategoryDataChange }) => {
  const { palette, Colors } = useAppTheme();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openRentEditDialog, setOpenRentEditDialog] = useState(false);
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, unit: null });
  const [newUnitData, setNewUnitData] = useState({ number: '', status: 'Vacant', tenant: null });
  const [editUnitData, setEditUnitData] = useState({ id: null, number: '' });
  const [editRentData, setEditRentData] = useState({ id: null, rent: 0, unitNumber: '' });
  const [generateData, setGenerateData] = useState({ namingScheme: 'prefix-number', unitPrefix: 'A', startNumber: 1, endNumber: 10 });

  useEffect(() => { if (selectedCategory) loadUnits(); }, [selectedCategory]);

  const loadUnits = async () => {
    try { setLoading(true); setUnits(await fetchUnits(selectedCategory)); setError(null); }
    catch (err) { setError('Failed to load units: ' + err.message); }
    finally { setLoading(false); }
  };

  const toast = (msg) => { setSuccessMessage(msg); setTimeout(() => setSuccessMessage(null), 3000); };
  const errToast = (msg) => { setError(msg); setTimeout(() => setError(null), 3000); };

  const handleSubmitUnit = async () => {
    try { setLoading(true); await createUnit(selectedCategory, newUnitData); setOpenUnitDialog(false); setNewUnitData({ number: '', status: 'Vacant', tenant: null }); await loadUnits(); onCategoryDataChange(); toast('Unit created successfully'); }
    catch (err) { setError('Failed to create unit: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleSubmitEdit = async () => {
    if (!editUnitData.number.trim()) { errToast('Unit name cannot be empty'); return; }
    try { setLoading(true); await updateUnit(editUnitData.id, { number: editUnitData.number }); setOpenEditDialog(false); await loadUnits(); toast('Unit name updated successfully'); }
    catch (err) { errToast('Failed to update unit: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleSubmitRentEdit = async () => {
    if (editRentData.rent < 0) { errToast('Rent cannot be negative'); return; }
    try { setLoading(true); await updateUnit(editRentData.id, { rent: editRentData.rent }); setOpenRentEditDialog(false); await loadUnits(); toast('Unit rent updated successfully'); }
    catch (err) { errToast('Failed to update unit rent: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleToggleAvailability = async (unit) => {
    if (unit.status !== 'Vacant') { errToast('Only vacant units can have their availability changed'); return; }
    try {
      setLoading(true); const newVal = !unit.availableForBooking;
      await updateUnit(unit.id, { availableForBooking: newVal }); await loadUnits();
      toast(`Unit ${unit.number} is now ${newVal ? 'available' : 'unavailable'} for booking`);
    } catch (err) { errToast('Failed to update availability: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleDeleteUnit = async () => {
    const unit = deleteConfirmDialog.unit; if (!unit) return;
    try {
      setLoading(true); await deleteUnit(unit.id); await loadUnits(); onCategoryDataChange();
      setDeleteConfirmDialog({ open: false, unit: null }); toast(`Unit ${unit.number} deleted successfully`);
    } catch (err) { errToast('Failed to delete unit: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleNameUnits = async () => {
    try {
      setLoading(true);
      const unnamed = units.filter(u => u.number.startsWith('Unit-'));
      const num = Math.min(generateData.endNumber - generateData.startNumber + 1, unnamed.length);
      for (let i = 0; i < num; i++) {
        const newNumber = generateData.namingScheme === 'prefix-number'
          ? `${generateData.unitPrefix}${parseInt(generateData.startNumber) + i}`
          : `${parseInt(generateData.startNumber) + i}`;
        await updateUnit(unnamed[i].id, { number: newNumber });
      }
      setOpenGenerateDialog(false); await loadUnits(); toast(`${num} units named successfully`);
    } catch (err) { errToast('Failed to name units: ' + err.message); }
    finally { setLoading(false); }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Occupied':    return { backgroundColor: Colors.status.success, color: '#fff' };
      case 'Vacant':      return { backgroundColor: Colors.primary.main,   color: '#fff' };
      case 'Maintenance': return { backgroundColor: Colors.status.warning, color: '#fff' };
      case 'Reserved':    return { backgroundColor: Colors.accent.purple,  color: '#fff' };
      default:            return { backgroundColor: palette.text.muted,    color: '#fff' };
    }
  };

  const getAvailabilityBadge = (unit) => {
    if (unit.status === 'Occupied') return (
      <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full"
        style={{ backgroundColor: `${palette.text.muted}20`, color: palette.text.muted }}>
        <XCircle className="w-3 h-3" /> Not Available
      </span>
    );
    if (unit.isAvailable) return (
      <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full"
        style={{ backgroundColor: `${Colors.status.success}15`, color: Colors.status.success }}>
        <CheckCircle className="w-3 h-3" /> Available
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full"
        style={{ backgroundColor: `${Colors.status.error}15`, color: Colors.status.error }}>
        <XCircle className="w-3 h-3" /> Unavailable
      </span>
    );
  };

  const currentCategory = currentApartment?.categories?.find(c => c.id === selectedCategory);
  const P   = Colors.primary.main;
  const bg  = palette.background;
  const txt = palette.text;
  const bdr = palette.border;

  const modalOverlay = { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' };
  const modalBox     = { backgroundColor: bg.secondary, border: `1px solid ${bdr}`, borderRadius: '12px', maxWidth: '448px', width: '100%', padding: '24px' };
  const inputSt      = { width: '100%', padding: '8px 12px', border: `1px solid ${bdr}`, borderRadius: '8px', outline: 'none', backgroundColor: bg.tertiary, color: txt.primary };
  const focusSt      = { borderColor: P, boxShadow: `0 0 0 2px ${P}30` };
  const cancelBtn    = { padding: '8px 16px', borderRadius: '8px', transition: 'all 0.2s', border: `1px solid ${bdr}`, backgroundColor: bg.tertiary, color: txt.primary };
  const primaryBtn   = { flex: 1, padding: '8px 16px', backgroundColor: P, color: '#fff', borderRadius: '8px', transition: 'all 0.2s' };
  const dangerBtn    = { flex: 1, padding: '8px 16px', backgroundColor: Colors.status.error, color: '#fff', borderRadius: '8px' };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg.primary }}>
      {/* Header */}
      <div className="rounded-xl shadow-sm p-6 mb-6" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {[['Back to Categories', handleBackToCategories], ['Back to Apartments', handleBackToApartments]].map(([label, fn]) => (
            <button key={label} onClick={fn} className="flex items-center gap-2 font-medium transition px-3 py-1.5 rounded-md"
              style={{ color: txt.secondary, border: `1px solid ${bdr}` }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
              <ChevronLeft className="w-4 h-4" />{label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold mb-2" style={{ color: P }}>
              {currentApartment?.name} › {currentCategory?.name}
            </h1>
            <p style={{ color: txt.secondary }}>
              Total Units: {currentCategory?.totalUnits || 0} • Vacant: {currentCategory?.vacantUnits || 0}
            </p>
          </div>
          {currentCategory?.unnamedUnits > 0 && (
            <button onClick={() => setOpenGenerateDialog(true)}
              className="text-white font-semibold px-6 py-2.5 rounded-lg flex items-center gap-2 shadow-sm transition"
              style={{ backgroundColor: P }}>
              <Edit className="w-5 h-5" /> Name Units ({currentCategory.unnamedUnits})
            </button>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="rounded-xl p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: `${Colors.status.success}10`, border: `1px solid ${Colors.status.success}40` }}>
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.success }} />
          <p className="font-medium" style={{ color: Colors.status.success }}>{successMessage}</p>
        </div>
      )}
      {error && (
        <div className="rounded-xl p-4 mb-6 flex items-start gap-3" style={{ backgroundColor: `${Colors.status.error}10`, border: `1px solid ${Colors.status.error}40` }}>
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: Colors.status.error }} />
          <p className="font-medium" style={{ color: Colors.status.error }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="rounded-xl shadow-sm p-12 text-center mb-6" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: P }}></div>
          <p className="mt-4" style={{ color: txt.secondary }}>Loading units...</p>
        </div>
      )}

      {!loading && (
        <div className="rounded-xl shadow-sm overflow-hidden" style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: bg.tertiary, borderBottom: `1px solid ${bdr}` }}>
                <tr>
                  {['Unit ID','House Name','Status','Availability','Base Rent','Tenant','Actions'].map(col => (
                    <th key={col} className={`px-6 py-4 text-xs font-bold uppercase ${col === 'Actions' ? 'text-center' : 'text-left'}`} style={{ color: txt.secondary }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {units.length === 0 ? (
                  <tr><td colSpan="7" className="px-6 py-12 text-center" style={{ color: txt.muted }}>No units found for this category</td></tr>
                ) : units.map(unit => (
                  <tr key={unit.id} style={{ borderBottom: `1px solid ${bdr}` }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = bg.tertiary}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td className="px-6 py-4 text-sm font-semibold" style={{ color: txt.primary }}>{unit.apartmentUnitId || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: txt.primary }}>{unit.number}</span>
                        <button onClick={() => { setEditUnitData({ id: unit.id, number: unit.number }); setOpenEditDialog(true); }}
                          className="p-1 rounded transition" style={{ color: P }} title="Edit name">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full" style={getStatusStyle(unit.status)}>{unit.status}</span>
                    </td>
                    <td className="px-6 py-4">{getAvailabilityBadge(unit)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium" style={{ color: txt.primary }}>{formatCurrency(unit.rent || 0)}</span>
                        <button onClick={() => { setEditRentData({ id: unit.id, rent: unit.rent || 0, unitNumber: unit.number }); setOpenRentEditDialog(true); }}
                          className="p-1 rounded transition" style={{ color: P }} title="Edit rent">
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm" style={{ color: txt.primary }}>{unit.tenant || '—'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {unit.status === 'Vacant' && (
                          <>
                            <button onClick={() => handleToggleAvailability(unit)}
                              className="p-2 rounded-lg transition"
                              style={{ color: unit.availableForBooking ? Colors.status.warning : Colors.status.success }}
                              title={unit.availableForBooking ? 'Mark as unavailable' : 'Mark as available'}>
                              {unit.availableForBooking ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                            <button onClick={() => setDeleteConfirmDialog({ open: true, unit })}
                              className="p-2 rounded-lg transition" style={{ color: Colors.status.error }} title="Delete unit">
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Unit Name Dialog */}
      {openEditDialog && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold" style={{ color: txt.primary }}>Edit Unit Name</h2>
              <button onClick={() => setOpenEditDialog(false)} style={{ color: txt.muted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Unit Name</label>
              <input type="text" value={editUnitData.number} onChange={e => setEditUnitData({ ...editUnitData, number: e.target.value })}
                style={inputSt} placeholder="e.g., A101"
                onFocus={e => Object.assign(e.target.style, focusSt)} onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setOpenEditDialog(false)} style={{ ...cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleSubmitEdit} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Rent Dialog */}
      {openRentEditDialog && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold" style={{ color: txt.primary }}>Edit Rent — {editRentData.unitNumber}</h2>
              <button onClick={() => setOpenRentEditDialog(false)} style={{ color: txt.muted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Monthly Rent (KES)</label>
              <input type="number" value={editRentData.rent} onChange={e => setEditRentData({ ...editRentData, rent: parseFloat(e.target.value) || 0 })}
                style={inputSt} placeholder="15000" min="0"
                onFocus={e => Object.assign(e.target.style, focusSt)} onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setOpenRentEditDialog(false)} style={{ ...cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleSubmitRentEdit} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Name Units Dialog */}
      {openGenerateDialog && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold" style={{ color: txt.primary }}>Name Units</h2>
              <button onClick={() => setOpenGenerateDialog(false)} style={{ color: txt.muted }}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Naming Scheme</label>
                <select value={generateData.namingScheme} onChange={e => setGenerateData({ ...generateData, namingScheme: e.target.value })}
                  style={inputSt} onFocus={e => Object.assign(e.target.style, focusSt)} onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }}>
                  <option value="prefix-number">Prefix + Number (e.g., A1, A2)</option>
                  <option value="number-only">Number Only (e.g., 1, 2)</option>
                </select>
              </div>
              {generateData.namingScheme === 'prefix-number' && (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>Unit Prefix</label>
                  <input type="text" value={generateData.unitPrefix} onChange={e => setGenerateData({ ...generateData, unitPrefix: e.target.value })}
                    style={inputSt} placeholder="A" onFocus={e => Object.assign(e.target.style, focusSt)} onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {[['Start Number', 'startNumber', 1], ['End Number', 'endNumber', 1]].map(([label, key, min]) => (
                  <div key={key}>
                    <label className="block text-sm font-medium mb-2" style={{ color: txt.secondary }}>{label}</label>
                    <input type="number" value={generateData[key]} min={min}
                      onChange={e => setGenerateData({ ...generateData, [key]: parseInt(e.target.value) || min })}
                      style={inputSt} onFocus={e => Object.assign(e.target.style, focusSt)} onBlur={e => { e.target.style.borderColor = bdr; e.target.style.boxShadow = ''; }} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setOpenGenerateDialog(false)} style={{ ...cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleNameUnits} disabled={loading} style={{ ...primaryBtn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Applying...' : 'Apply Names'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      {deleteConfirmDialog.open && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 rounded-full" style={{ backgroundColor: `${Colors.status.error}15` }}>
                <AlertCircle className="w-6 h-6" style={{ color: Colors.status.error }} />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2" style={{ color: txt.primary }}>Delete Unit</h2>
                <p style={{ color: txt.secondary }}>
                  Are you sure you want to delete unit <strong>{deleteConfirmDialog.unit?.number}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmDialog({ open: false, unit: null })} style={{ ...cancelBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleDeleteUnit} disabled={loading} style={{ ...dangerBtn, opacity: loading ? 0.5 : 1 }}>
                {loading ? 'Deleting...' : 'Delete Unit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitManagement;